import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import SearchIcon from 'assets/u_search-plus.svg?react'
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FixedSizeList as List } from 'react-window'
import styled, { css } from 'styled-components'
import { useDebouncedInput } from 'utils/hookHelpers'

import { DropdownSearchButton } from './DropdownSearchButton'
import {
  DropdownSearchMenuContext,
  DropdownSearchMenuItem,
  FilteredSearchContainer,
  StyledDropdownContent,
} from './SearchSortFilter'

const StyledInput = styled.input`
  position: relative;
  color: ${({ theme }) => theme.forecast.activeText};
  background-color: ${({ theme }) => theme.forecast.background1};
  border: 1px solid transparent;
  padding-left: 1.75rem;

  ::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText} !important;
  }

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.forecast.background4};
    outline-offset: 2px;
  }
`
const SearchIconContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 0.375rem;
  padding-right: 0.75rem;
  fill: ${({ theme }) => theme.forecast.icon1};
`

const commonDropdownHeaderStyles = css`
  display: block;
  align-items: center;
  text-align: left;
  padding: 0;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: '14px';
  line-height: '16px';
  font-weight: medium;
  user-select: none;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DropdownHeader = styled.span`
  ${commonDropdownHeaderStyles}
`

const StyledStretchyInputDiv = styled.div`
  flex-grow: 1;
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
`

const StyledInputContainer = styled.div`
  position: relative;
  display: flex;
  gap: 10px;
`

export const IconSpacer = styled.div`
  width: 22px;
`

export const DropdownSearchContext = createContext<{
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}>({
  open: false,
  setOpen: () => {
    // noop
  },
})

export default function DropdownSearch({
  children,
}: {
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <DropdownSearchContext.Provider value={{ open, setOpen }}>
      <div>
        <DropdownMenu.Root open={open} onOpenChange={setOpen}>
          {children}
        </DropdownMenu.Root>
      </div>
    </DropdownSearchContext.Provider>
  )
}

DropdownSearch.Trigger = DropdownSearchButton

export function DropdownSearchMenu({
  children,
}: {
  children: React.ReactNode
}) {
  const { open, setOpen } = useContext(DropdownSearchContext)
  const firstItemRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const firstRender = useRef(true)

  const [searchText, setSearchText] = useState('')

  const { debouncedResults, searchTerm, setSearchTerm } = useDebouncedInput(300)

  useEffect(() => {
    setSearchText(searchTerm)
  }, [searchTerm])

  const controls = useAnimationControls()

  async function closeMenu() {
    await controls.start('closed')
    setSearchText('')
    setSearchTerm('')
    setOpen(false)
  }

  useEffect(() => {
    const handleOpen = async () => {
      if (open) {
        setSearchTerm('')
        await controls.start('open')
        inputRef.current?.focus()
      }
    }
    handleOpen()
  }, [controls, open, setSearchTerm])

  const handleKeyPress = (e: KeyboardEvent) => {
    if (
      e.key === 'ArrowDown' ||
      (e.key === 'ArrowUp' && firstRender?.current)
    ) {
      firstItemRef?.current?.focus()
      firstRender.current = false
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  return (
    <DropdownSearchMenuContext.Provider
      value={{
        closeMenu,
        firstItemRef,
        inputRef,
        debouncedResults,
        searchText,
      }}>
      <AnimatePresence>
        {open && (
          <DropdownMenu.Portal forceMount>
            <StyledDropdownContent align='start' asChild>
              <motion.div
                initial={false}
                animate={controls}
                exit='closed'
                variants={{
                  open: {
                    opacity: 1,
                    transition: { ease: 'easeOut', duration: 0.1 },
                  },
                  closed: {
                    opacity: 0,
                    transition: { ease: 'easeIn', duration: 0.2 },
                  },
                }}>
                {children}
              </motion.div>
            </StyledDropdownContent>
          </DropdownMenu.Portal>
        )}
      </AnimatePresence>
    </DropdownSearchMenuContext.Provider>
  )
}

DropdownSearch.Menu = DropdownSearchMenu

export function DropdownSearchLabel({
  children,
}: {
  children: React.ReactNode
}) {
  return <DropdownHeader>{children}</DropdownHeader>
}

DropdownSearch.Label = DropdownSearchLabel

export function FilteredSearch<T>({
  listToFilter,
  itemGetter,
  itemSearchTerms,
  placeholder,
  onSelect,
  extra,
}: {
  listToFilter: T[]
  itemGetter: (item: T, closeMenu: () => Promise<void>) => ReactNode
  itemSearchTerms?: (item: T) => string[]
  placeholder?: string
  onSelect: (item?: T) => void
  extra?: (props: { closeMenu(): Promise<void> }) => React.ReactNode
}) {
  const { inputRef, debouncedResults, searchText, closeMenu } = useContext(
    DropdownSearchMenuContext
  )
  const { open } = useContext(DropdownSearchContext)
  const [hasUsedArrows, setHasUsedArrows] = useState(false)

  const filteredList = useMemo(() => {
    if (!searchText) return listToFilter
    const lowerCaseSearch = searchText.toLowerCase()
    return listToFilter.filter((item) => {
      const searchTerms = itemSearchTerms?.(item) ?? []
      searchTerms.concat(itemGetter(item, closeMenu) as string)
      return searchTerms.some((term) =>
        term.toLowerCase().includes(lowerCaseSearch)
      )
    })
  }, [searchText, listToFilter, itemSearchTerms, itemGetter, closeMenu])

  // Reset arrow navigation state whenever the search text changes or the menu opens
  useEffect(() => {
    setHasUsedArrows(false)
  }, [searchText, open])

  return (
    open && (
      <FilteredSearchContainer>
        <StyledInputContainer>
          <StyledStretchyInputDiv>
            <StyledInput
              onChange={debouncedResults}
              placeholder={placeholder}
              onKeyDown={(e) => {
                // Mark when user starts navigating with arrows
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  setHasUsedArrows(true)
                }

                // Allow quick-select of first item with Enter while typing
                if (e.key === 'Enter') {
                  if (filteredList.length > 0) {
                    e.preventDefault()
                    e.stopPropagation()
                    // Select the first filtered item
                    onSelect(filteredList[0])
                    void closeMenu()
                  }
                  return
                }

                // Stop propagation for non-navigation keys so typing doesn't close the menu
                if (
                  e.key !== 'Escape' &&
                  e.key !== 'Tab' &&
                  e.key !== 'ArrowDown' &&
                  e.key !== 'ArrowUp'
                ) {
                  e.stopPropagation()
                }
              }}
              ref={inputRef}
            />
            <SearchIconContainer>
              <SearchIcon />
            </SearchIconContainer>
          </StyledStretchyInputDiv>
          {extra && extra({ closeMenu })}
        </StyledInputContainer>
        <List
          // if the list is short, we don't need to expand the dropdown all the way
          height={filteredList?.length < 10 ? filteredList?.length * 50 : 315}
          itemCount={filteredList?.length}
          itemSize={45}
          width={500}>
          {({ index, style }) => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                ...style,
              }}>
              <DropdownSearchMenuItem
                key={index}
                isFirstItem={index === 0}
                isSelected={!hasUsedArrows && index === 0}
                onSelect={() => onSelect(filteredList[index])}>
                {itemGetter(filteredList[index], closeMenu)}
              </DropdownSearchMenuItem>
            </div>
          )}
        </List>
      </FilteredSearchContainer>
    )
  )
}

DropdownSearch.FilteredSearch = FilteredSearch

DropdownSearch.MenuItem = DropdownSearchMenuItem
